/**
 * Wraps an element in another element.
 * @param {Node} element Element to wrap.
 * @param {Node} wrapper Parent element to use as wrapper.
 */
function wrapElement(element, wrapper) {
    element.parentNode.insertBefore(wrapper, element);
    wrapper.appendChild(element);
}

/**
 * Inserts an element as the last child element of a parent element.
 * @param {Node} parent Parent element.
 * @param {Node} child Child element to append.
 */
function appendElement(parent, child) {
    parent.appendChild(child);
}

/**
 * Inserts an element as the first child element of a parent element.
 * @param {Node} parent Parent element.
 * @param {Node} child Child element to append.
 */
function prependElement(parent, child) {
    if (parent.childNodes.length > 0) {
        parent.insertBefore(child, parent.childNodes[0]);
    }
    else {
        parent.appendChild(child);
    }
}

/**
 * Creates and inserts an element as the last child element of a parent element.
 * @param {string} tagName Tag name of the element to create.
 * @param {Node} parent Parent element to insert element to.
 * @returns {Node} The created element.
 */
function createElement(tagName, parent) {
    const el = document.createElement(tagName);

    if (parent) {
        parent.appendChild(el);
    }

    return el;
}

/**
 * Removes an element from the DOM tree.
 * @param {Node} element The element to remove.
 */
function removeElement(element) {
    element.parentNode.removeChild(element);
}

/**
 * Gets the first following sibling element that matches a selector.
 * @param {Node} element Element to use.
 * @param {string} selector CSS Selector.
 * @returns {Node} Sibling element.
 */
function getNextSibling(element, selector) {

    // Get the next sibling element
    let sibling = element.nextElementSibling;

    // If there's no selector, return the first sibling
    if (!selector)
        return sibling;

    // If the sibling matches our selector, use it
    // If not, jump to the next sibling and continue the loop
    while (sibling) {
        if (sibling.matches(selector))
            return sibling;

        sibling = sibling.nextElementSibling
    }

}

/**
 * Gets the first previous sibling element that matches a selector.
 * @param {Node} element Element to use.
 * @param {string} selector CSS Selector.
 * @returns {Node} Sibling element.
 */
function getPrevSibling(element, selector) {

    // Get the next sibling element
    let sibling = element.previousElementSibling;

    // If there's no selector, return the first sibling
    if (!selector)
        return sibling;

    // If the sibling matches our selector, use it
    // If not, jump to the next sibling and continue the loop
    while (sibling) {
        if (sibling.matches(selector))
            return sibling;

        sibling = sibling.previousElementSibling;
    }

}

/**
 * Sets multiple style properties of an element.
 * @param {Node} element Element to use.
 * @param {Object.<string, string>} styles CSS styles.
 */
function setStyles(element, styles) {
    for (let s in styles) {
        element.style[s] = styles[s];
    }
}

/**
 * Adds event listener to a list of elements.
 * @param {any} nodeListOrSelector A node list or CSS selector.
 * @param {string} eventName Name of the event.
 * @param {Function} fn The function.
 * @param {boolean} [useCapture=false] useCapture 
 */
function addEventListeners(nodeListOrSelector, eventName, fn, useCapture = false) {

    if (typeof nodeListOrSelector === 'string') {
        nodeListOrSelector = document.querySelectorAll(nodeListOrSelector);
    }

    if (!nodeListOrSelector)
        return;

    for (let i = 0, len = nodeListOrSelector.length; i < len; i++) {
        nodeListOrSelector[i].addEventListener(eventName, fn, useCapture);
    }
}

/**
 * Removes event listener from a list of elements.
 * @param {any} nodeListOrSelector A node list or CSS selector.
 * @param {any} eventName Name of the event.
 * @param {any} fn The function.
 */
function removeEventListeners(nodeListOrSelector, eventName, fn) {

    if (typeof nodeListOrSelector === 'string') {
        nodeListOrSelector = document.querySelectorAll(nodeListOrSelector);
    }

    if (!nodeListOrSelector)
        return;

    for (let i = 0, len = nodeListOrSelector.length; i < len; i++) {
        nodeListOrSelector[i].removeEventListener(eventName, fn);
    }
}

/**
 * Adds a dynamic event listner for child elements.
 * @param {string} eventName Name of the event.
 * @param {any} parent Parent Node or CSS selector to attach event listener to.
 * @param {string} target CSS selector for child elements that should trigger the event.
 * @param {Function} fn The function.
 * @param {boolean} [useCapture=false] useCapture
 */
function addDynamicEventListener(eventName, parent, target, fn, useCapture = false) {
    let parentElement;

    if (typeof parent === "string") {
        parentElement = document.querySelector(parent);
    } else {
        parentElement = parent;
    }

    if (!parentElement || !target)
        return;

    parentElement.addEventListener(eventName,
        function (e) {

            if (e.target && e.target.matches && e.target.matches(target)) {
                fn.call(target, e);
            }

        },
        useCapture
    );
}

/**
 * Gets the last element that matches a CSS selector.
 * @param {string} selector CSS selector.
 * @param {Node} root Root element to use.
 * @returns {Node} The last element that matches the selector.
 */
function querySelectorLast(selector, root = document) {
    const elements = root.querySelectorAll(selector);

    if (elements === null || elements.length === 0)
        return null;

    return elements[elements.length - 1];
}

export {
    appendElement,
    prependElement,
    createElement,
    getNextSibling,
    getPrevSibling,
    wrapElement,
    removeElement,
    setStyles,
    addEventListeners,
    removeEventListeners,
    addDynamicEventListener,
    querySelectorLast
};